import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { History, Search, AlertCircle } from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { formatDateTime } from '../../lib/dateFmt';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { api, ApiError } from '../../lib/api';

interface Row {
  id: string;
  actor_id: string | null;
  actor_email: string;
  request_method: string;
  request_path: string;
  status_code: number;
  category: string;
  actor_ip: string | null;
  created_at: string;
}

const ALLOWED = ['admin', 'super_admin'];
const CATEGORIES = ['', 'signature', 'person', 'admin', 'magic-link-submit'];

/**
 * Read-only viewer for the access_log table (CLAUDE.md §11).
 */
export function AccessLog() {
  const { data: user, isLoading } = useCurrentUser();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('');
  const [actorEmail, setActorEmail] = useState<string>('');

  useEffect(() => {
    if (!user || !user.roles.some((r) => ALLOWED.includes(r))) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        if (actorEmail) params.set('actor_email', actorEmail);
        const r = await api<{ rows: Row[] }>(
          `/api/admin/access-log${params.size > 0 ? `?${params.toString()}` : ''}`,
        );
        if (!cancelled) setRows(r.rows);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'load failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, category, actorEmail]);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Access log is restricted to admin / super_admin.
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-danger-soft/15 text-danger-soft-fg">
            <History className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-danger-soft-fg">
              Audit
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Access log
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Audit trail for sensitive reads and writes by authenticated
              users. Emergency-mode actions live in the separate
              emergency-access log; this is the complement for ordinary
              sessions.
            </p>
          </div>
        </header>

        <div className="mt-6 flex flex-wrap items-end gap-3">
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1.5 block h-10 rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c || 'all'}
                </option>
              ))}
            </select>
          </label>
          <label className="block flex-1 text-sm">
            <span className="font-medium text-fg-muted">Actor email</span>
            <div className="relative mt-1.5">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim" />
              <input
                type="search"
                value={actorEmail}
                onChange={(e) => setActorEmail(e.target.value)}
                placeholder="filter by email"
                className="block h-10 w-full rounded-md border border-line-strong/20 bg-surface pl-9 pr-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
              />
            </div>
          </label>
        </div>

        {error ? (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          {rows === null ? (
            <SkeletonList rows={6} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<History className="h-6 w-6" />}
              title="Nothing logged"
              description="Nothing matches this filter."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-muted">
                  <tr>
                    <th className="px-4 py-2.5">When</th>
                    <th className="px-4 py-2.5">Actor</th>
                    <th className="px-4 py-2.5">Category</th>
                    <th className="px-4 py-2.5">Method</th>
                    <th className="px-4 py-2.5">Path</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-line/10 transition-colors hover:bg-surface-2/50"
                    >
                      <td className="px-4 py-2.5 align-top text-xs tabular-nums text-fg-muted">
                        {formatDateTime(r.created_at)}
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs">{r.actor_email}</td>
                      <td className="px-4 py-2.5 align-top text-xs">
                        <span className="rounded bg-surface-2 px-2 py-0.5 font-mono text-[10px] font-semibold text-fg-muted">
                          {r.category}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs font-mono">
                        {r.request_method}
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs">
                        <code className="break-all font-mono">{r.request_path}</code>
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs">
                        <span
                          className={`inline-flex items-center rounded px-2 py-0.5 font-mono ${
                            r.status_code >= 400
                              ? 'bg-danger-soft/15 text-danger-soft-fg'
                              : 'bg-success-soft/15 text-success-soft-fg'
                          }`}
                        >
                          {r.status_code}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs font-mono text-fg-muted">
                        {r.actor_ip ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
